import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircleOutlined, WhatsApp } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import SectionContainer from '../components/SectionContainer';
import RevealOnScroll from '../components/RevealOnScroll';
import { submitLibraryLead } from '../leadService';
import useAnalytics from '../../analytics/useAnalytics';
import { EVT_LIBRARY_LEAD_SUBMIT } from '../../analytics/events';
import { INDIAN_CITIES } from '../../utils/indianCities';

const DEFAULT_TRUST_BULLETS = [
  'Free to list',
  '24-hour onboarding',
  'No long-term contracts',
];

const DEFAULT_WA_URL =
  'https://wa.me/918595675155?text=Hi%20BookMyLibrary%2C%20I%27m%20interested%20in%20listing%20my%20library';

const PHONE_RE = /^[6-9]\d{9}$/;

const EMPTY_FORM = {
  ownerName: '',
  phone: '',
  city: null,
};

const EMPTY_ERRORS = {
  ownerName: '',
  phone: '',
  city: '',
};

function validate(fields) {
  const errs = { ...EMPTY_ERRORS };
  if (!fields.ownerName || fields.ownerName.trim().length < 2) {
    errs.ownerName = 'Name must be at least 2 characters.';
  } else if (fields.ownerName.trim().length > 100) {
    errs.ownerName = 'Name must be under 100 characters.';
  }
  if (!fields.phone || !PHONE_RE.test(fields.phone)) {
    errs.phone = 'Enter a valid 10-digit Indian mobile number.';
  }
  if (!fields.city) {
    errs.city = 'Please select a city.';
  }
  return errs;
}

function hasErrors(errs) {
  return Object.values(errs).some(Boolean);
}

function SignupSection({
  eyebrow,
  title,
  subtitle,
  pitchTitle,
  pitchBody,
  whatsappCtaLabel,
  whatsappUrl,
  trustBullets,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { track } = useAnalytics();
  const formRef = useRef(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [submitting, setSubmitting] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  useEffect(() => {
    function handleWebsiteAddon(e) {
      if (e.detail === true && formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    window.addEventListener('bml:toggle-website-addon', handleWebsiteAddon);
    return () => window.removeEventListener('bml:toggle-website-addon', handleWebsiteAddon);
  }, []);

  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;

  const cardBg = isDark ? 'rgba(27,54,93,0.6)' : theme.palette.background.paper;
  const cardBorder = isDark
    ? '1px solid rgba(255,255,255,0.08)'
    : '1px solid rgba(27,54,93,0.08)';

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (hasErrors(errs)) return;

    setSubmitting(true);
    const payload = {
      name: form.ownerName.trim(),
      phone: form.phone,
      city: form.city,
    };
    await submitLibraryLead(payload);
    track(EVT_LIBRARY_LEAD_SUBMIT, { city: payload.city });
    setSubmitting(false);
    setSnackOpen(true);
    setForm(EMPTY_FORM);
    setErrors(EMPTY_ERRORS);
  };

  return (
    <SectionContainer
      id="signup"
      eyebrow={eyebrow}
      title={title}
      subtitle={subtitle}
      sx={{
        background: isDark
          ? `radial-gradient(ellipse at 80% 50%, rgba(242,101,34,0.08) 0%, transparent 70%), ${theme.palette.background.default}`
          : `radial-gradient(ellipse at 80% 50%, rgba(242,101,34,0.06) 0%, transparent 70%), ${theme.palette.background.default}`,
      }}
    >
      <Grid container spacing={{ xs: 4, md: 8 }} alignItems="flex-start">
        {/* Left: pitch column */}
        <Grid item xs={12} md={5}>
          <RevealOnScroll>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}>
                {pitchTitle}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.75, mb: 4 }}>
                {pitchBody}
              </Typography>

              <Button
                variant="outlined"
                component="a"
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsApp />}
                sx={{
                  borderColor: '#25D366',
                  color: '#25D366',
                  fontWeight: 700,
                  borderWidth: 2,
                  mb: 4,
                  '&:hover': {
                    borderColor: '#1DA851',
                    color: '#1DA851',
                    background: 'rgba(37,211,102,0.06)',
                    borderWidth: 2,
                  },
                }}
              >
                {whatsappCtaLabel}
              </Button>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {trustBullets.map((bullet) => (
                  <Box key={bullet} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircleOutlined sx={{ color: orange, fontSize: 20, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {bullet}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </RevealOnScroll>
        </Grid>

        {/* Right: form card */}
        <Grid item xs={12} md={7}>
          <RevealOnScroll delay={0.1}>
            <Box
              ref={formRef}
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: '24px',
                background: cardBg,
                border: cardBorder,
                boxShadow: isDark
                  ? '0 8px 40px rgba(0,0,0,0.3)'
                  : '0 8px 40px rgba(27,54,93,0.1)',
              }}
            >
              <Grid container spacing={2.5}>
                {/* Owner name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Your name"
                    value={form.ownerName}
                    onChange={set('ownerName')}
                    error={Boolean(errors.ownerName)}
                    helperText={errors.ownerName}
                    fullWidth
                    required
                    inputProps={{ 'aria-label': 'Owner name' }}
                  />
                </Grid>

                {/* Phone */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Mobile number"
                    value={form.phone}
                    onChange={set('phone')}
                    error={Boolean(errors.phone)}
                    helperText={errors.phone}
                    fullWidth
                    required
                    inputProps={{
                      inputMode: 'numeric',
                      maxLength: 10,
                      'aria-label': 'Mobile number',
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">+91</InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* City */}
                <Grid item xs={12}>
                  <Autocomplete
                    options={INDIAN_CITIES}
                    value={form.city}
                    onChange={(_e, val) =>
                      setForm((prev) => ({ ...prev, city: val }))
                    }
                    freeSolo={false}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="City"
                        required
                        error={Boolean(errors.city)}
                        helperText={errors.city}
                        inputProps={{
                          ...params.inputProps,
                          'aria-label': 'City',
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Submit */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={submitting}
                    sx={{
                      background: `linear-gradient(135deg, ${orange}, ${golden})`,
                      color: '#fff',
                      fontWeight: 700,
                      py: 1.75,
                      fontSize: '1rem',
                      borderRadius: 3,
                      boxShadow: '0 8px 24px rgba(242,101,34,0.35)',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.brand.orange.light}, ${orange})`,
                        boxShadow: '0 12px 32px rgba(242,101,34,0.45)',
                        transform: submitting ? 'none' : 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease',
                    }}
                    startIcon={
                      submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : null
                    }
                  >
                    {submitting ? 'Submitting…' : 'Submit details'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </RevealOnScroll>
        </Grid>
      </Grid>

      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          We received your details. Our team will call you within 24 hours.
        </Alert>
      </Snackbar>
    </SectionContainer>
  );
}

SignupSection.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  pitchTitle: PropTypes.string,
  pitchBody: PropTypes.string,
  whatsappCtaLabel: PropTypes.string,
  whatsappUrl: PropTypes.string,
  trustBullets: PropTypes.arrayOf(PropTypes.string),
};

SignupSection.defaultProps = {
  eyebrow: 'Get started',
  title: 'List your library with BookMyLibrary.',
  subtitle: "Fill the form. We'll call you within 24 hours.",
  pitchTitle: 'Join hundreds of libraries growing with BML.',
  pitchBody:
    "Our team will set everything up for you. You just need to fill the form and we'll take care of the rest.",
  whatsappCtaLabel: 'Chat with us on WhatsApp',
  whatsappUrl: DEFAULT_WA_URL,
  trustBullets: DEFAULT_TRUST_BULLETS,
};

export default SignupSection;
